import {
  doc,
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  arrayRemove,
  arrayUnion,
  onSnapshot
} from 'firebase/firestore'
import { DB } from '../../configs/firebase'
import { formatDateForDB } from '../../utils/date'

export default {
  namespaced: true,
  state: {
    comment: {
      id: null,
      uid: null,
      user: {
        name: null,
        image: null
      },
      body: null,
      date: null,
      likes: [],
      comments: 0
    },
    comments: [],
    reportedComments: [],
    lastCommentRef: null,
    nonReportedSubscriber: null,
    reportedSubscriber: null
  },
  getters: {
    COMMENTS: (state) => state.comments,
    REPORTED_COMMENTS: (state) => state.reportedComments
  },
  mutations: {
    SET_COMMENTS(state, payload) {
      state.comments = payload
    },
    ADD_COMMENT(state, payload) {
      state.comments.push(payload)
    },
    EDIT_COMMENT(state, payload) {
      const index = state.comments.findIndex(
        (comment) => comment.id === payload.id
      )
      state.comments.splice(index, 1, payload)
    },
    DELETE_COMMENT(state, id) {
      const index = state.comments.findIndex((comment) => comment.id === id)
      state.comments.splice(index, 1)
    },
    SET_LAST_COMMENT(state, payload) {
      state.lastCommentRef = payload
    }
  },
  actions: {
    async GET_TOTAL({ dispatch }, topicId) {
      const q = query(
        collection(DB, 'comments'),
        where('topicId', '==', topicId),
        where('isReported', '==', false)
      )
      const result = await getDocs(q)

      let counter = 0;
      // we only need to count comments that has an existing owner
      for (const doc of result.docs) {
        const comment = doc.data();
        const user = await dispatch('users/GET', comment.uid, { root: true })
        if (user) {
          counter += 1;
        }
      }
      return counter;
    },
    async SUBSCRIBE_TO_COMMENTS({ state, commit, dispatch }, topicId) {
      const commentsCollection = collection(DB, 'comments')
      const queryByTopic = where('topicId', '==', topicId)
      const notReportedComments = where('isReported', '==', false);
      const queryOrder = orderBy('date', 'desc')
      const q = query(commentsCollection, queryByTopic, notReportedComments, queryOrder)

      if (state.nonReportedSubscriber) {
        return;
      }

      state.comments = [];
      state.nonReportedSubscriber = onSnapshot(q,
        async (snapshot) => {
          const docs = snapshot.docChanges();
          for (const data of docs) {
            const comment = Object.assign({}, { id: data.doc.id }, {...data.doc.data() });

            if (data.type === 'added') {
              const user = await dispatch('users/GET', comment.uid, { root: true })
              if (user) {
                comment.user = {
                  name: user.name,
                  image: user.image
                }
                commit('ADD_COMMENT', comment)
              }
            } else if (data.type === 'modified') {
              const user = await dispatch('users/GET', comment.uid, { root: true })
              if (user) {
                comment.user = {
                  name: user.name,
                  image: user.image
                }
                commit('EDIT_COMMENT', comment)
              }
            } else if (data.type === 'removed') {
              commit('DELETE_COMMENT', comment.id);
            }
          }
        }
      )
    },
    async UNSUBSCRIBE_TO_COMMENTS({ state }) {
      if (state.nonReportedSubscriber) {
        await state.nonReportedSubscriber();
        state.nonReportedSubscriber = null;
      }
    },
    async SUBSCRIBE_TO_REPORTED({ state, dispatch }) {
      if (state.reportedSubscriber) {
        return;
      }

      state.reportedComments = [];
      const q = query(
        collection(DB, 'comments'),
        where('isReported', '==', true),
        orderBy('reportedAt', 'desc')
      )
      state.reportedSubscriber = onSnapshot(q,
        async (snapshot) => {
          const docs = snapshot.docChanges();

          for (const data of docs) {
            const comment = Object.assign({}, { id: data.doc.id }, { ...data.doc.data() });
            console.log(comment, data);

            if (comment.uid) {
              const user = await dispatch('users/GET', comment.uid, { root: true });
              comment.user = {
                name: user.name,
                image: user.image
              }
            }

            if (comment.reportedBy) {
              const user = await dispatch('users/GET', comment.reportedBy, { root: true });
              comment.reporter = {
                name: user.name,
                image: user.image
              }
            }

            if (comment.topicId) {
              const topic = await dispatch('cms/GET_TOPIC', comment.topicId, { root: true });
              comment.topic = topic;
            }

            switch(data.type) {
              case 'added': {
                state.reportedComments.push(comment)
                break;
              }

              case 'modified': {
                const index = state.reportedComments.findIndex(c => c.id === comment.id);
                if (index >= 0) {
                  state.reportedComments.splice(index, 1, comment);
                }
                break;
              }

              case 'removed': {
                const index = state.reportedComments.findIndex(c => c.id === comment.id);
                if (index >= 0) {
                  state.reportedComments.splice(index, 1);
                }
                break;
              }

              default: {
                console.log('');
              }
            }
          }
        }
      )
    },
    async UNSUBSCRIBE_TO_REPORTED({ state }) {
      if (state.reportedSubscriber) {
        await state.reportedSubscriber()
        state.reportedSubscriber = null;
      }
    },
    async ADD_COMMENT({ rootGetters }, { body, topicId }) {
      if (!body || !topicId) {
        return
      }

      const user = rootGetters['users/USER']

      const comment = {
        uid: user.id,
        topicId,
        body,
        likes: [],
        date: formatDateForDB(new Date()),
        isReported: false
      }
      const docRef = collection(DB, 'comments')
      await addDoc(docRef, comment)
    },
    async UPDATE_COMMENT(_, { id, body }) {
      if (!id || !body) {
        return
      }

      const docRef = doc(DB, 'comments', id)
      await updateDoc(docRef, { body })
    },
    async DELETE_COMMENT(_, { id }) {
      if (!id) {
        return
      }

      const docRef = doc(DB, 'comments', id)
      await deleteDoc(docRef)
    },
    async TOGGLE_LIKE({ rootGetters }, { id }) {
      if (!id) {
        return
      }

      const docRef = doc(DB, 'comments', id)
      let commentRef = await getDoc(docRef)
      let comment = commentRef.data()
      comment.id = commentRef.id

      const user = rootGetters['users/USER']

      // if the user likes the post, then remove its uid from the likes array
      // otherwise, add the current user to the likes array
      await updateDoc(docRef, {
        likes: comment.likes.includes(user.id)
          ? arrayRemove(user.id)
          : arrayUnion(user.id)
      })
    },
    async REPORT_COMMENT({ rootGetters }, { id, reportMessage }) {
      if (!id || !reportMessage) {
        return;
      }

      const docRef = doc(DB, 'comments', id)
      const user = rootGetters['users/USER']

      await updateDoc(docRef, {
        isReported: true,
        reportMessage,
        reportedBy: user.id,
        reportedAt: formatDateForDB(new Date())
      })
    },
    async REAPPROVE_COMMENT(_, { id }) {
      if (!id) {
        return;
      }

      const docRef = doc(DB, 'comments', id)

      await updateDoc(docRef, {
        isReported: false,
      })
    }
  }
}
